import React, { useState } from 'react';
import { Phone, Send, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { contactInfo, detailedServices } from '../mockData';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const Devis = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    marque: '',
    modele: '',
    immatriculation: '',
    message: ''
  });
  const [selectedServices, setSelectedServices] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Compte les services sélectionnés
    const servicesCount = Object.values(selectedServices).filter(Boolean).length;

    if (servicesCount === 0) {
      toast.error('Veuillez sélectionner au moins un service');
      setIsSubmitting(false);
      return;
    }

    // Récupérer les labels des services sélectionnés
    const selectedServiceLabels = [];
    detailedServices.forEach(category => {
      category.items.forEach(item => {
        if (selectedServices[item.id]) {
          selectedServiceLabels.push(`${category.title} - ${item.label}`);
        }
      });
    });

    // Préparer les données pour l'API
    const requestData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      marque: formData.marque,
      modele: formData.modele,
      immatriculation: formData.immatriculation,
      selected_services: selectedServiceLabels,
      message: formData.message
    };

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/devis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        toast.success('Demande de devis envoyée !', {
          description: `Nous vous contacterons très bientôt. ${servicesCount} service(s) sélectionné(s).`,
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          marque: '',
          modele: '',
          immatriculation: '',
          message: ''
        });
        setSelectedServices({});
      } else {
        toast.error('Erreur lors de l\'envoi', {
          description: data.message || 'Veuillez réessayer plus tard.'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion', {
        description: 'Impossible de contacter le serveur. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = Object.values(selectedServices).filter(Boolean).length;

  return (
    <div className="devis-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="devis-hero">
        <div className="container">
          <div className="devis-hero-content fade-in">
            <h1 className="page-title">
              Demande de <span className="text-yellow">Devis</span><br />
              Personnalisé
            </h1>
            <p className="page-subtitle">
              Sélectionnez les prestations souhaitées et recevez un devis gratuit et détaillé sous 24h
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="devis-form-section section-padding">
        <div className="container">
          <form onSubmit={handleSubmit} className="devis-form">
            {/* Informations Client */}
            <div className="form-section">
              <h2 className="form-section-title">
                <CheckCircle size={24} />
                Vos informations
              </h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nom complet *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Votre nom"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="votre@email.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Téléphone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>
            </div>

            {/* Informations Véhicule */}
            <div className="form-section">
              <h2 className="form-section-title">
                <CheckCircle size={24} />
                Votre véhicule
              </h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="marque" className="form-label">Marque *</label>
                  <input
                    type="text"
                    id="marque"
                    name="marque"
                    value={formData.marque}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Ex: Renault"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="modele" className="form-label">Modèle *</label>
                  <input
                    type="text"
                    id="modele"
                    name="modele"
                    value={formData.modele}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Ex: Clio 5"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="immatriculation" className="form-label">Immatriculation</label>
                  <input
                    type="text"
                    id="immatriculation"
                    name="immatriculation"
                    value={formData.immatriculation}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="AB-123-CD"
                  />
                </div>
              </div>
            </div>

            {/* Sélection Services */}
            <div className="form-section">
              <h2 className="form-section-title">
                <CheckCircle size={24} />
                Sélectionnez vos prestations
                {selectedCount > 0 && (
                  <span className="selected-badge">{selectedCount} sélectionné(s)</span>
                )}
              </h2>
              <div className="services-selection">
                {detailedServices.map((category) => (
                  <div key={category.id} className="service-category">
                    <button
                      type="button"
                      className="category-header"
                      onClick={() => toggleCategory(category.id)}
                    >
                      <span className="category-title">{category.title}</span>
                      {expandedCategories[category.id] ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                    {expandedCategories[category.id] && (
                      <div className="category-items">
                        {category.items.map((item) => (
                          <label key={item.id} className="service-checkbox-label">
                            <input
                              type="checkbox"
                              checked={selectedServices[item.id] || false}
                              onChange={() => handleServiceToggle(item.id)}
                              className="service-checkbox"
                            />
                            <span className="checkbox-custom"></span>
                            <span className="service-label-text">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Message */}
            <div className="form-section">
              <h2 className="form-section-title">
                <CheckCircle size={24} />
                Informations complémentaires
              </h2>
              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="5"
                  placeholder="Décrivez votre besoin, ajoutez des précisions..."
                ></textarea>
              </div>
            </div>

            <button type="submit" className="btn-primary form-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
              <Send size={20} />
            </button>
          </form>

          {/* Contact rapide */}
          <div className="quick-contact">
            <p className="quick-contact-text">Besoin d'une réponse immédiate ?</p>
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="btn-secondary">
              <Phone size={20} />
              Appelez-nous : {contactInfo.phone}
            </a>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Hero */
        .devis-hero {
          background: linear-gradient(135deg, var(--bg-page) 0%, var(--bg-card) 100%);
          padding: 160px 40px 80px;
          border-bottom: 3px solid var(--brand-yellow);
        }

        .devis-hero-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }

        .page-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: clamp(2.5rem, 6vw, 5rem);
          line-height: 1.1;
          color: var(--text-primary);
          margin-bottom: 24px;
        }

        .page-subtitle {
          font-size: clamp(1rem, 2vw, 1.3rem);
          line-height: 1.7;
          color: var(--text-secondary);
          max-width: 700px;
          margin: 0 auto;
        }

        /* Form */
        .devis-form {
          max-width: 1000px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        .form-section {
          background: var(--bg-card);
          padding: 32px;
          border-radius: var(--radius-lg);
          border: 2px solid var(--border-light);
        }

        .form-section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.5rem;
          color: var(--brand-yellow);
          margin-bottom: 24px;
        }

        .selected-badge {
          margin-left: auto;
          background: var(--brand-yellow);
          color: var(--brand-black);
          padding: 4px 12px;
          border-radius: var(--radius-pill);
          font-size: 0.9rem;
          font-weight: 600;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text-primary);
        }

        .form-input,
        .form-textarea {
          background: var(--bg-page);
          border: 2px solid var(--border-light);
          border-radius: var(--radius-md);
          padding: 14px 16px;
          font-family: 'Inter', sans-serif;
          font-size: 1rem;
          color: var(--text-primary);
          transition: all 0.3s ease;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--brand-yellow);
          box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 120px;
        }

        /* Services Selection */
        .services-selection {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .service-category {
          background: var(--bg-page);
          border: 2px solid var(--border-light);
          border-radius: var(--radius-md);
          overflow: hidden;
        }

        .category-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: transparent;
          border: none;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .category-header:hover {
          background: var(--bg-card);
        }

        .category-title {
          font-weight: 700;
          font-size: 1.1rem;
        }

        .category-items {
          padding: 16px 20px;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
          border-top: 1px solid var(--border-light);
        }

        .service-checkbox-label {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          padding: 8px;
          border-radius: var(--radius-sm);
          transition: background 0.2s ease;
        }

        .service-checkbox-label:hover {
          background: var(--bg-card);
        }

        .service-checkbox {
          display: none;
        }

        .checkbox-custom {
          width: 20px;
          height: 20px;
          border: 2px solid var(--border-medium);
          border-radius: 4px;
          flex-shrink: 0;
          transition: all 0.3s ease;
          position: relative;
        }

        .service-checkbox:checked + .checkbox-custom {
          background: var(--brand-yellow);
          border-color: var(--brand-yellow);
        }

        .service-checkbox:checked + .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: var(--brand-black);
          font-weight: 900;
          font-size: 14px;
        }

        .service-label-text {
          font-size: 0.95rem;
          color: var(--text-secondary);
        }

        .service-checkbox:checked ~ .service-label-text {
          color: var(--text-primary);
          font-weight: 600;
        }

        .form-submit {
          width: 100%;
          margin-top: 20px;
        }

        /* Quick Contact */
        .quick-contact {
          max-width: 1000px;
          margin: 40px auto 0;
          text-align: center;
          padding: 32px;
          background: var(--bg-card);
          border-radius: var(--radius-lg);
          border: 2px solid var(--brand-yellow);
        }

        .quick-contact-text {
          font-size: 1.2rem;
          color: var(--text-primary);
          margin-bottom: 20px;
          font-weight: 600;
        }

        /* Responsive */
        @media (max-width: 767px) {
          .devis-hero {
            padding: 120px 20px 60px;
          }

          .form-section {
            padding: 24px 20px;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .category-items {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Devis;
